import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarRange } from "lucide-react";

const isoDate = (d) => d.toISOString().slice(0, 10);
const today = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const daysAgo = (n) => { const d = today(); d.setDate(d.getDate() - n); return d; };
const startOfMonth = () => { const d = today(); d.setDate(1); return d; };

const PRESETS = [
  { key: "today", label: "Today", start: () => today(), end: () => today() },
  { key: "7d", label: "Last 7 Days", start: () => daysAgo(6), end: () => today() },
  { key: "month", label: "This Month", start: () => startOfMonth(), end: () => today() },
];

/**
 * Global date range filter. Value = { from, to, preset }.
 * Emits `onChange({from,to,preset})` with ISO date strings.
 */
export default function DateRangeFilter({ value, onChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [range, setRange] = useState({ from: undefined, to: undefined });

  const applyPreset = (p) => {
    const s = p.start(), e = p.end();
    onChange({ from: isoDate(s), to: isoDate(e), preset: p.key });
  };

  const applyCustom = () => {
    if (range.from && range.to) {
      onChange({ from: isoDate(range.from), to: isoDate(range.to), preset: "custom" });
      setPickerOpen(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="date-range-filter">
      {PRESETS.map((p) => (
        <Button key={p.key}
          data-testid={`date-preset-${p.key}`}
          variant={value.preset === p.key ? "default" : "outline"}
          size="sm"
          className={value.preset === p.key ? "bg-sky-600 hover:bg-sky-700 text-white" : ""}
          onClick={() => applyPreset(p)}>
          {p.label}
        </Button>
      ))}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <Button variant={value.preset === "custom" ? "default" : "outline"} size="sm"
            className={value.preset === "custom" ? "bg-sky-600 hover:bg-sky-700 text-white gap-1.5" : "gap-1.5"}
            data-testid="date-preset-custom">
            <CalendarRange className="w-3.5 h-3.5" />
            {value.preset === "custom" ? `${value.from} → ${value.to}` : "Custom"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto p-0">
          <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={2} />
          <div className="p-3 border-t flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setRange({ from: undefined, to: undefined })}>Clear</Button>
            <Button size="sm" onClick={applyCustom} disabled={!range?.from || !range?.to} data-testid="date-custom-apply">Apply</Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
